import React, { useEffect, useState } from 'react';
import {
  addDoc,
  doc,
  updateDoc,
  collection,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthContext } from '../../context/AuthContext';
import { Drill } from '../../types/Drill';
import { DrillCategory } from '../../types/DrillCategory';

const MAX_EQUIPMENT = 20;

interface Props {
  existingDrill: Drill | null;
  onClose: () => void;
  onSaved: (categoryId: number) => void;
}

const DrillForm: React.FC<Props> = ({ existingDrill, onClose, onSaved }) => {
  const { user } = useAuthContext();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState<number>(10);
  const [categoryId, setCategoryId] = useState<number>(0); // 0 = unselected
  const [equipment, setEquipment] = useState<string[]>(['']);
  const [categories, setCategories] = useState<DrillCategory[]>([]);
  const [error, setError] = useState('');

  // Load categories from Firestore
  useEffect(() => {
    const fetchCategories = async () => {
      const snap = await getDocs(collection(db, 'drillCategories'));
      const items = snap.docs.map(doc => ({
        id: parseInt(doc.id),
        ...(doc.data() as Omit<DrillCategory, 'id'>),
      }));
      setCategories(items);
    };

    fetchCategories();
  }, []);

  // Pre-fill form when editing
  useEffect(() => {
    if (existingDrill) {
      setName(existingDrill.name || '');
      setDescription(existingDrill.description || '');
      setDuration(existingDrill.duration || 10);
      setCategoryId(Number(existingDrill.categoryId) || 0);
      setEquipment(
        Array.isArray(existingDrill.equipment)
          ? [...existingDrill.equipment, '']
          : ['']
      );
    } else {
      setEquipment(['']);
    }
  }, [existingDrill]);

  // Handle dynamic equipment entry
  const handleEquipmentChange = (index: number, value: string) => {
    const updated = [...equipment];
    updated[index] = value;
    if (
      index === updated.length - 1 &&
      value.trim() !== '' &&
      updated.length < MAX_EQUIPMENT
    ) {
      updated.push('');
    }
    setEquipment(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const cleanedEquipment = equipment.filter(e => e.trim() !== '');
    if (!name || !description || categoryId === 0) {
      setError('Please complete all required fields.');
      return;
    }

    const drillData = {
      name,
      description,
      duration,
      categoryId,
      equipment: cleanedEquipment,
      isDefault: false,
      teamId: user?.teamId || '',
    };

    try {
      if (existingDrill) {
        const ref = doc(db, `teams/${user?.teamId}/drills`, existingDrill.id);
        await updateDoc(ref, drillData);
      } else {
        await addDoc(collection(db, `teams/${user?.teamId}/drills`), drillData);
      }
      onSaved(categoryId); // Notify Training page
    } catch (err) {
      console.error(err);
      setError('Error saving drill.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className='bg-white shadow p-4 rounded mb-4'>
      <h2 className='text-lg font-bold mb-4'>
        {existingDrill ? 'Edit Drill' : 'New Drill'}
      </h2>

      <div className='grid grid-cols-1 gap-4'>
        <div>
          <label className='block text-sm font-medium'>Name:</label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            className='w-full border p-2'
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium'>Description:</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            className='w-full border p-2'
            rows={4}
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium'>Duration (mins):</label>
          <input
            type='number'
            value={duration}
            onChange={e => setDuration(Number(e.target.value))}
            className='w-full border p-2'
            min={1}
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium'>Category:</label>
          <select
            value={categoryId}
            onChange={e => setCategoryId(Number(e.target.value))}
            className='w-full border p-2'
            required
          >
            <option value={0}>Please choose a category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className='block text-sm font-medium'>Equipment:</label>
          {equipment.map((item, index) => (
            <input
              key={index}
              value={item}
              onChange={e => handleEquipmentChange(index, e.target.value)}
              className='w-full border p-2 mb-1'
              placeholder={`Item ${index + 1}`}
            />
          ))}
        </div>

        {error && <p className='text-red-500 text-sm'>{error}</p>}

        <div className='flex justify-end gap-2'>
          <button
            type='button'
            onClick={onClose}
            className='bg-gray-500 text-white px-4 py-2 rounded'
          >
            Cancel
          </button>
          <button
            type='submit'
            className='bg-green-600 text-white px-4 py-2 rounded'
          >
            Save Drill
          </button>
        </div>
      </div>
    </form>
  );
};

export default DrillForm;
